import PhotoSphere from '@/common/components/photo-sphere';
import { VehicleCarousel, VehicleCarouselProps } from './vehicle-carousel';
import { AspectRatio } from '@/shadcn-ui/aspect-ratio';
import SwitchWithText from '@/common/components/switch-with-text';
import { useState } from 'react';
import Spacer from '@/common/components/spacer';
import { cn } from '@/shadcn-ui/utils';
import { colors } from '@/theme/colors';
import Picture from '@/icons/picture';

interface VehicleImagesProps extends VehicleCarouselProps {
  interiorPhotoSphereUrl?: string;
}

export default function VehicleImages({
  vehicleImages,
  displayViewCount,
  viewCount,
  interiorPhotoSphereUrl,
}: VehicleImagesProps) {
  const [isShowingInterior360, setIsShowingInterior360] = useState(false);
  const [isPhotoSphereFullscreen, setIsPhotoSphereFullscreen] = useState(false);

  /** Due to oddities with Safari iOS fullscreen, we need to communicate when the photo sphere enters fullscreen to hide the controls as they otherwise appear on top */
  const photoSphereFullscreenDelegate = (isFullscreen: boolean) => {
    setIsPhotoSphereFullscreen(isFullscreen);
  };

  /** NOTE: absolute positioning, z index and opacity are used here for smoother animation transitioning and preloading of complex UI elements **/
  return (
    <div className="relative">
      <AspectRatio ratio={4 / 3} className="relative h-full w-full">
        <VehicleCarousel
          vehicleImages={vehicleImages}
          displayViewCount={displayViewCount}
          viewCount={viewCount}
          className={cn(
            'absolute inset-0 transition-opacity duration-300 ease-in-out will-change-[opacity]',
            {
              'z-0 overflow-hidden opacity-0': isShowingInterior360,
              'z-1 opacity-100': !isShowingInterior360,
            }
          )}
        />

        {interiorPhotoSphereUrl ? (
          <div
            className={cn(
              'absolute inset-0 transition-opacity duration-300 ease-in-out will-change-[opacity]',
              {
                'z-0 overflow-hidden opacity-0': !isShowingInterior360,
                'z-1 opacity-100': isShowingInterior360,
              }
            )}
          >
            <PhotoSphere
              url={interiorPhotoSphereUrl}
              fullscreenEventDelegate={photoSphereFullscreenDelegate}
            />
          </div>
        ) : null}

        {interiorPhotoSphereUrl && !isPhotoSphereFullscreen ? (
          <div className="pointer-events-none absolute right-[16px] top-0 z-1 flex flex-col items-center md:inset-0">
            <Spacer variant="height16" />
            <SwitchWithText
              offText={
                <p className="body3-strong flex items-center justify-center px-[16px] py-[8px] text-center">
                  <Picture
                    fillPrimary={!isShowingInterior360 ? colors.blue40 : colors.neutral1}
                  />
                </p>
              }
              onText={
                <p
                  className={cn(
                    'body3-strong flex items-center justify-center px-[16px] py-[8px] text-center',
                    {
                      'text-flexcar-blue40': isShowingInterior360,
                      'text-flexcar-neutral1': !isShowingInterior360,
                    }
                  )}
                >
                  Interior 360°
                </p>
              }
              checkedBackgroundColor="data-[state=checked]:bg-flexcar-neutral90/30"
              uncheckedBackgroundColor="data-[state=unchecked]:bg-flexcar-neutral90/30"
              thumbClassName="shadow-sm"
              checked={isShowingInterior360}
              onCheckedChange={(checked) => setIsShowingInterior360(checked)}
              className="pointer-events-auto border-[1.5px] border-solid border-flexcar-neutral80 backdrop-blur-[10px]"
            />
          </div>
        ) : null}
      </AspectRatio>
    </div>
  );
}
